import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { inr, num, pnlColor } from "@/lib/format";
import { toast } from "sonner";
import { Activity, Play, Pause, Square, XCircle, Plus } from "lucide-react";
import { visibleInterval } from "@/lib/poll";

const STATUS_CLS = {
  active: "bg-[#00C805]/15 text-[#00C805]", paused: "bg-[#FFCC00]/15 text-[#FFCC00]",
  stopped: "bg-[#94A3B8]/15 text-slate-300", draft: "bg-[#2563EB]/15 text-[#5b9bff]",
  completed: "bg-[#475569]/20 text-slate-400",
};

export default function ActiveStrategies() {
  const [list, setList] = useState([]);
  const nav = useNavigate();

  const load = () => api.get("/strategies").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); return visibleInterval(load, 4000); }, []);

  const act = async (id, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await api.post(`/strategies/${id}/${action}`); load(); toast.success(action + " done"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this strategy?")) return;
    try { await api.delete(`/strategies/${id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><Activity className="h-5 w-5 text-[#2563EB]" /> Strategies</h1>
        <button onClick={() => nav("/builder")} className="text-xs font-semibold px-3 py-1.5 rounded-sm bg-[#2563EB] hover:bg-[#1d4fd0] flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> New Strategy</button>
      </div>

      {list.length === 0 && <div className="term-panel rounded-sm p-8 text-center text-sm text-slate-500">No strategies yet. Build one from the Explorer or Strategy Builder.</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((s) => (
          <div key={s.id} className="term-panel rounded-sm p-4 space-y-3 hover:border-[#3a4150] cursor-pointer" onClick={() => nav(`/strategies/${s.id}`)} data-testid={`strategy-card-${s.symbol}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-sm">{s.name}</div>
                <div className="text-[11px] text-slate-500 mono">{s.symbol} · BUY {s.buy_expiry} / SELL {s.sell_expiry}</div>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm uppercase ${STATUS_CLS[s.status]}`}>{s.status}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><div className="text-slate-500">Matched Lots</div><div className="mono">{num(s.total_matched_lots, 0)}</div></div>
              <div><div className="text-slate-500">Wavg Spread</div><div className="mono">₹{num(s.weighted_avg_spread, 2)}</div></div>
              <div><div className="text-slate-500">Net P&L</div><div className={`mono ${pnlColor(s.net_pnl)}`}>{s.net_pnl != null ? inr(s.net_pnl) : "—"}</div></div>
            </div>
            {s.status !== "completed" && s.status !== "draft" && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-slate-500">Cur Spread </span><span className="mono">₹{num(s.current_spread, 2)}</span></div>
                <div><span className="text-slate-500">Next Trig </span><span className="mono">{s.next_trigger_spread ? "₹" + s.next_trigger_spread : "—"}</span></div>
              </div>
            )}
            <div className="flex flex-wrap gap-1.5 pt-1" onClick={(e) => e.stopPropagation()}>
              {s.status === "draft" && <button onClick={() => act(s.id, "start")} className="btn-a" data-testid={`start-${s.symbol}`}><Play className="h-3 w-3" /> Start</button>}
              {s.status === "active" && <button onClick={() => act(s.id, "pause")} className="btn-a"><Pause className="h-3 w-3" /> Pause Levels</button>}
              {(s.status === "paused" || s.status === "stopped") && <button onClick={() => act(s.id, "start")} className="btn-a"><Play className="h-3 w-3" /> Resume</button>}
              {s.status === "active" && <button onClick={() => act(s.id, "stop")} className="btn-a"><Square className="h-3 w-3" /> Stop New</button>}
              {["active", "paused", "stopped"].includes(s.status) && <button onClick={() => act(s.id, "square-off", "Square off this strategy now?")} className="btn-danger" data-testid={`squareoff-${s.symbol}`}><XCircle className="h-3 w-3" /> Square Off</button>}
              {["draft", "completed"].includes(s.status) && <button onClick={() => del(s.id)} className="btn-a text-[#FF6b62]">Delete</button>}
            </div>
          </div>
        ))}
      </div>
      <style>{`.btn-a{display:inline-flex;align-items:center;gap:4px;padding:4px 8px;border-radius:2px;background:#1A1D24;border:1px solid #2A2F3A;font-weight:600}.btn-a:hover{background:#232730}.btn-danger{display:inline-flex;align-items:center;gap:4px;padding:4px 8px;border-radius:2px;background:rgba(255,59,48,.15);color:#FF6b62;border:1px solid rgba(255,59,48,.4);font-weight:700}`}</style>
    </div>
  );
}
